<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import GroupsAPI from 'dashboard/api/masifiWhatsappQrGroups';
import { isMasifiWaGroupContact } from 'dashboard/helper/masifiWaGroups';
import {
  WHATSAPP_QR_FEATURE,
  isWhatsappQrInbox,
} from 'dashboard/helper/masifiWhatsappQr';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';

// "Grupos de WhatsApp" de una PERSONA (E2E fix batch 1, decisión de Marcos 2026-09-24): los grupos de la cuenta en los
// que está, su rol, "Llegó por el grupo X" y el enlace al chat del grupo cuando quien mira puede abrirlo (el servidor lo
// decide con la política nativa). Solo en un inbox QR con el módulo activo; incluido desde MasifiWaGroupPanel (0
// touchpoints core nuevos).
const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const router = useRouter();
const currentChat = useMapGetter('getSelectedChat');
const getInbox = useMapGetter('inboxes/getInbox');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

const isPersonInQr = computed(
  () =>
    !isMasifiWaGroupContact(props.contact) &&
    !!isFeatureEnabled.value?.(accountId.value, WHATSAPP_QR_FEATURE) &&
    isWhatsappQrInbox(getInbox.value?.(currentChat.value?.inbox_id))
);
const isOpen = ref(true);
const groups = ref([]);
const origin = ref(null);
const total = ref(0);
const page = ref(1);

// Revisión E2E fix 1, M4: cada carga de una conversación lleva su turno; la respuesta (o el error) de una conversación
// anterior, más lenta, no pisa la actual al cambiar de conversación rápido.
let generation = 0;
const fetchPage = async nextPage => {
  const turn = generation;
  const { data } = await GroupsAPI.contactGroups(props.conversationId, {
    page: nextPage,
  });
  if (turn !== generation) return;
  groups.value =
    nextPage === 1 ? data.payload : [...groups.value, ...data.payload];
  if (nextPage === 1) origin.value = data.origin;
  total.value = data.meta.total;
  page.value = nextPage;
};

const load = async () => {
  generation += 1;
  const turn = generation;
  groups.value = [];
  origin.value = null;
  total.value = 0;
  if (!isPersonInQr.value) return;
  try {
    await fetchPage(1);
  } catch (error) {
    if (turn !== generation) return;
    groups.value = [];
    origin.value = null;
  }
};

watch(() => [props.conversationId, isPersonInQr.value], load, {
  immediate: true,
});

const visible = computed(
  () => isPersonInQr.value && (groups.value.length > 0 || !!origin.value)
);
const hasMore = computed(() => groups.value.length < total.value);
const roleLabel = role =>
  t(`MASIFI_WHATSAPP_QR.GROUPS.PANEL.ROLE.${(role || 'member').toUpperCase()}`);
const isAdminRole = role => ['admin', 'superadmin'].includes(role);
const open = id =>
  router.push(frontendURL(conversationUrl({ accountId: accountId.value, id })));
const loadMore = () => fetchPage(page.value + 1).catch(() => {});
</script>

<template>
  <div v-if="visible" class="px-2 pb-2">
    <AccordionItem
      :title="t('MASIFI_WHATSAPP_QR.GROUPS.CONTACT.TITLE')"
      :is-open="isOpen"
      @toggle="isOpen = !isOpen"
    >
      <div class="flex flex-col gap-3 text-sm">
        <p v-if="origin" class="text-n-slate-11">
          {{
            t('MASIFI_WHATSAPP_QR.GROUPS.CONTACT.ORIGIN', { name: origin.name })
          }}
        </p>
        <ul class="flex flex-col gap-2">
          <li
            v-for="group in groups"
            :key="group.id"
            class="flex items-center gap-2 min-w-0"
          >
            <Avatar
              :name="group.name"
              :src="group.thumbnail || ''"
              :size="24"
            />
            <span class="truncate text-n-slate-12">{{ group.name }}</span>
            <Label
              v-if="isAdminRole(group.role)"
              color="blue"
              :label="roleLabel(group.role)"
              compact
            />
            <Button
              v-if="group.conversation_id"
              data-test="masifi-wa-contact-group-open"
              link
              xs
              slate
              icon="i-lucide-message-circle"
              class="ltr:ml-auto rtl:mr-auto"
              :title="t('MASIFI_WHATSAPP_QR.GROUPS.CONTACT.OPEN')"
              :label="t('MASIFI_WHATSAPP_QR.GROUPS.CONTACT.OPEN')"
              @click="open(group.conversation_id)"
            />
          </li>
        </ul>
        <div v-if="hasMore">
          <Button
            data-test="masifi-wa-contact-groups-more"
            link
            sm
            :label="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.LOAD_MORE')"
            @click="loadMore"
          />
        </div>
      </div>
    </AccordionItem>
  </div>
</template>
